import {
  V3ApiResponse,
  PaginationParams,
  PaginatedResponse,
  buildPaginationQuery,
  CreatePortfolioRequest,
  UpdatePortfolioRequest,
  PatchPortfolioRequest,
  PortfolioCompositionRequest,
  PortfolioDetail,
  PortfolioListItem,
  PortfolioContractState,
  PublishPortfolioResult,
  VersionListItem,
  VersionCommentMeta,
  VersionMetadataItem,
  CreateVersionMetadataRequest,
  AuditLogEntry,
  PortfolioBasketState,
  ProductSearchItem,
  PortfolioActiveComposition,
  PortfolioAfterActivationResult,
  PortfolioCompositionChangeEntry,
  PaginatedCompositionChanges,
} from "../types/productV3";
import {
  PendingContractsData,
  PortfolioPendingContract,
  CancelPendingActivationsResult,
  LockStatusResult,
} from "../types/pendingContracts";
import httpService, { HttpError } from "./httpService";

const BASE_PATH = "/v3/products/portfolios";
const SEARCH_PATH = "/v3/products/search/provider";

class PortfoliosV3Service {
  async createPortfolio(
    data: CreatePortfolioRequest
  ): Promise<PortfolioDetail> {
    try {
      const response = await httpService.post<V3ApiResponse<PortfolioDetail>>(
        BASE_PATH,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 create portfolio error:", error);
      throw error;
    }
  }

  async updatePortfolio(
    portfolioId: number,
    data: UpdatePortfolioRequest
  ): Promise<PortfolioDetail> {
    try {
      const response = await httpService.put<V3ApiResponse<PortfolioDetail>>(
        `${BASE_PATH}/${portfolioId}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 update portfolio error:", error);
      throw error;
    }
  }

  async patchPortfolio(
    portfolioId: number,
    data: PatchPortfolioRequest
  ): Promise<PortfolioDetail> {
    try {
      const response = await httpService.patch<V3ApiResponse<PortfolioDetail>>(
        `${BASE_PATH}/${portfolioId}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 patch portfolio error:", error);
      throw error;
    }
  }

  async updateComposition(
    portfolioId: number,
    data: PortfolioCompositionRequest
  ): Promise<PortfolioDetail> {
    try {
      const response = await httpService.put<V3ApiResponse<PortfolioDetail>>(
        `${BASE_PATH}/${portfolioId}/composition`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 update portfolio composition error:", error);
      throw error;
    }
  }

  async publishPortfolio(
    portfolioId: number
  ): Promise<PublishPortfolioResult> {
    try {
      const response = await httpService.post<
        V3ApiResponse<PublishPortfolioResult>
      >(`${BASE_PATH}/${portfolioId}/publish`);
      return response.data.data;
    } catch (error) {
      console.error("V3 publish portfolio error:", error);
      throw error;
    }
  }

  async getPortfolio(portfolioId: number): Promise<PortfolioDetail> {
    try {
      const response = await httpService.get<V3ApiResponse<PortfolioDetail>>(
        `${BASE_PATH}/${portfolioId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 get portfolio error:", error);
      throw error;
    }
  }

  async getActiveSnapshot(
    portfolioId: number
  ): Promise<ProductSearchItem | null> {
    try {
      const response = await httpService.get<
        PaginatedResponse<ProductSearchItem>
      >(`${SEARCH_PATH}?productType=PORTFOLIO&productId=${portfolioId}&pageSize=1`);
      const items = response.data.items ?? [];
      return items.length > 0 ? items[0] : null;
    } catch (error) {
      console.error("V3 get portfolio active snapshot error:", error);
      return null;
    }
  }

  async getContractStates(
    portfolioId: number
  ): Promise<PortfolioContractState[]> {
    try {
      const response = await httpService.get<
        V3ApiResponse<PortfolioContractState[]>
      >(`${BASE_PATH}/${portfolioId}/contract-states`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get portfolio contract states error:", error);
      throw error;
    }
  }

  async getVersions(portfolioId: number): Promise<VersionListItem[]> {
    try {
      const response = await httpService.get<
        V3ApiResponse<VersionListItem[]>
      >(`${BASE_PATH}/${portfolioId}/versions`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get portfolio versions error:", error);
      throw error;
    }
  }

  async getPortfolios(
    params?: PaginationParams
  ): Promise<PaginatedResponse<PortfolioListItem>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<
        PaginatedResponse<PortfolioListItem>
      >(`${BASE_PATH}${query}`);
      return response.data;
    } catch (error) {
      console.error("V3 get portfolios error:", error);
      throw error;
    }
  }

  async deletePortfolio(portfolioId: number): Promise<void> {
    try {
      await httpService.delete(`${BASE_PATH}/${portfolioId}`);
    } catch (error) {
      console.error("V3 delete portfolio error:", error);
      throw error;
    }
  }

  async getAuditLog(
    portfolioId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<AuditLogEntry>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<
        V3ApiResponse<PaginatedResponse<AuditLogEntry>>
      >(`${BASE_PATH}/${portfolioId}/user-updates${query}`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get portfolio audit log error:", error);
      throw error;
    }
  }

  /**
   * Returns per-basket activation states for a portfolio, or null if the
   * endpoint is not yet available (Q18). Callers must handle null --
   * null means "API unavailable" (hide basket activation indicators),
   * [] means "no basket states".
   */
  async getBasketStates(
    portfolioId: number
  ): Promise<PortfolioBasketState[] | null> {
    try {
      const response = await httpService.get<
        V3ApiResponse<PortfolioBasketState[]>
      >(`${BASE_PATH}/${portfolioId}/basket-states`);
      return response.data.data;
    } catch (error) {
      if ((error as HttpError).status === 404) return null;
      throw error;
    }
  }
  async getPendingContracts(
    portfolioId: number
  ): Promise<PendingContractsData<PortfolioPendingContract>> {
    try {
      const response = await httpService.get<
        V3ApiResponse<PendingContractsData<PortfolioPendingContract>>
      >(`${BASE_PATH}/${portfolioId}/pending-contracts`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get portfolio pending contracts error:", error);
      throw error;
    }
  }

  async cancelPendingActivations(
    portfolioId: number
  ): Promise<CancelPendingActivationsResult> {
    try {
      const response = await httpService.post<
        V3ApiResponse<CancelPendingActivationsResult>
      >(`${BASE_PATH}/${portfolioId}/cancel-pending-activations`);
      return response.data.data;
    } catch (error) {
      console.error("V3 cancel portfolio pending activations error:", error);
      throw error;
    }
  }

  async getLockStatus(portfolioId: number): Promise<LockStatusResult> {
    try {
      const response = await httpService.get<LockStatusResult>(
        `${BASE_PATH}/${portfolioId}/lock-status`
      );
      return response.data;
    } catch (error) {
      console.error("V3 get portfolio lock status error:", error);
      throw error;
    }
  }

  async getActiveComposition(portfolioId: number): Promise<PortfolioActiveComposition> {
    try {
      const response = await httpService.get<V3ApiResponse<PortfolioActiveComposition>>(
        `${BASE_PATH}/${portfolioId}/active`
      );
      return response.data.data;
    } catch (error) {
      // Retry once
      try {
        const response = await httpService.get<V3ApiResponse<PortfolioActiveComposition>>(
          `${BASE_PATH}/${portfolioId}/active`
        );
        return response.data.data;
      } catch (retryError) {
        console.error("V3 get portfolio active composition error:", retryError);
        throw retryError;
      }
    }
  }

  async getAfterActivation(portfolioId: number): Promise<PortfolioAfterActivationResult> {
    try {
      const response = await httpService.get<V3ApiResponse<PortfolioAfterActivationResult>>(
        `${BASE_PATH}/${portfolioId}/after-activation`
      );
      return response.data.data;
    } catch (error) {
      // Retry once
      try {
        const response = await httpService.get<V3ApiResponse<PortfolioAfterActivationResult>>(
          `${BASE_PATH}/${portfolioId}/after-activation`
        );
        return response.data.data;
      } catch (retryError) {
        console.error("V3 get portfolio after-activation error:", retryError);
        throw retryError;
      }
    }
  }

  async getChanges(portfolioId: number): Promise<PaginatedCompositionChanges<PortfolioCompositionChangeEntry>> {
    try {
      const response = await httpService.get<V3ApiResponse<PaginatedCompositionChanges<PortfolioCompositionChangeEntry>>>(
        `${BASE_PATH}/${portfolioId}/changes`
      );
      return response.data.data;
    } catch (error) {
      // Retry once
      try {
        const response = await httpService.get<V3ApiResponse<PaginatedCompositionChanges<PortfolioCompositionChangeEntry>>>(
          `${BASE_PATH}/${portfolioId}/changes`
        );
        return response.data.data;
      } catch (retryError) {
        console.error("V3 get portfolio changes error:", retryError);
        throw retryError;
      }
    }
  }

  // --- Version metadata (comments) — PWA-1666 ---

  async createVersionMetadata(
    portfolioId: number,
    versionId: number,
    metadata: VersionCommentMeta
  ): Promise<VersionMetadataItem> {
    try {
      const body: CreateVersionMetadataRequest = { metadata };
      const response = await httpService.post<V3ApiResponse<VersionMetadataItem>>(
        `${BASE_PATH}/${portfolioId}/versions/${versionId}/metadata`,
        body
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 create portfolio version metadata error:", error);
      throw error;
    }
  }

  async getVersionMetadata(
    portfolioId: number,
    versionId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<VersionMetadataItem>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${BASE_PATH}/${portfolioId}/versions/${versionId}/metadata${query}`
      );
      return response.data;
    } catch (error) {
      console.error("V3 get portfolio version metadata error:", error);
      throw error;
    }
  }

  async deleteVersionMetadata(
    portfolioId: number,
    versionId: number,
    metadataId: number
  ): Promise<void> {
    try {
      await httpService.delete(
        `${BASE_PATH}/${portfolioId}/versions/${versionId}/metadata/${metadataId}`
      );
    } catch (error) {
      console.error("V3 delete portfolio version metadata error:", error);
      throw error;
    }
  }

  async getAllVersionMetadata(
    portfolioId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<VersionMetadataItem>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${BASE_PATH}/${portfolioId}/version-metadata${query}`
      );
      return response.data;
    } catch (error) {
      console.error("V3 get all portfolio version metadata error:", error);
      throw error;
    }
  }
}

export const portfoliosV3Service = new PortfoliosV3Service();
